import classNames from 'classnames';
import { useMemo } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { RoutePath } from 'src/router';

import { useSelector, useDispatch } from 'src/store';
import { selectCompany } from 'src/store/slices/companySlice';
import {
  selectedCompanyIdSelector,
  allCompanyOptionsSelector,
  isLoadingCompanySelector,
} from 'src/store/selectors/companySelector';
import { isClientAdminSelector } from 'src/store/selectors/authSelector';

import { dropdownFilterOption } from '@itm/shared-frontend/lib/utils';
import Select, { SingleValue } from '@itm/shared-frontend/lib/components/react-select';

import { isAzureTargetPlatform } from 'src/utils/constants';

import { SelectOption } from 'src/types';

function Filters() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedCompanyId = useSelector(selectedCompanyIdSelector);
  const companyOptions = useSelector(allCompanyOptionsSelector);
  const isLoadingCompanies = useSelector(isLoadingCompanySelector);
  const isClientAdmin = useSelector(isClientAdminSelector);

  const migrationProjectsListRouteMatch = useMatch(RoutePath.migrationProjectsList);
  const migrationProjectsCreateRouteMatch = useMatch(RoutePath.migrationProjectsCreateDetails);
  const onboardingConfigurationsListRouteMatch = useMatch(RoutePath.onboardingConfigurationsList);
  const onboardingConfigurationsCreateRouteMatch = useMatch(RoutePath.onboardingConfigurationCreateDetails);
  const noDataTransformationAccessRouteMatch = useMatch(RoutePath.noDataTransformationAccess);

  // Client Admins have no Migration access, so they fall back to Data Onboarding
  const clientAdminFallbackRoute = isAzureTargetPlatform
    ? RoutePath.noDataTransformationAccess
    : RoutePath.onboardingConfigurationsList;
  const fallbackRoute = isClientAdmin ? clientAdminFallbackRoute : RoutePath.migrationProjectsList;

  const isFilterAvailable = useMemo(
    () =>
      Boolean(
        migrationProjectsListRouteMatch ||
        migrationProjectsCreateRouteMatch ||
        onboardingConfigurationsListRouteMatch ||
        onboardingConfigurationsCreateRouteMatch ||
        noDataTransformationAccessRouteMatch,
      ),
    [
      migrationProjectsListRouteMatch,
      migrationProjectsCreateRouteMatch,
      onboardingConfigurationsListRouteMatch,
      onboardingConfigurationsCreateRouteMatch,
      noDataTransformationAccessRouteMatch,
    ],
  );

  const selectedCompanyOption = useMemo(
    () => companyOptions.find((option) => option.value === selectedCompanyId) || null,
    [companyOptions, selectedCompanyId],
  );

  const companyChangeHandler = (newOption: SingleValue<SelectOption>) => {
    if (newOption) {
      dispatch(selectCompany(newOption.value));
      if (
        !migrationProjectsListRouteMatch &&
        !onboardingConfigurationsListRouteMatch &&
        !onboardingConfigurationsCreateRouteMatch
      ) {
        navigate(fallbackRoute);
      }
    }
  };

  return (
    <div className="is-flex is-justify-content-center pt-5">
      <div className="control is-fullwidth">
        <Select
          className={classNames('react-select', { 'is-loading': isLoadingCompanies })}
          classNamePrefix="react-select"
          value={selectedCompanyOption}
          options={companyOptions}
          aria-label="Company name"
          placeholder={companyOptions.length || isLoadingCompanies ? 'Company name' : 'No companies'}
          onChange={companyChangeHandler}
          isDisabled={!isFilterAvailable || isLoadingCompanies || !companyOptions.length}
          isClearable={false}
          isSearchable={true}
          closeMenuOnSelect={true}
          filterOption={dropdownFilterOption}
        />
      </div>
    </div>
  );
}

export default Filters;
